/**
 * vaultpod-viewer entry.
 * Mounts the SolidJS app + installs Tanstack-Query + Router.
 */
import "./index.css";
import { render } from "solid-js/web";

// Stamp the build id into the browser console on every page load.
// The string is replaced at bundle time (see vite.config.ts). If a
// screenshot shows an older SHA than the one deployed, the browser is
// running a cached bundle — index.html is served `no-store` so a
// simple reload should always pick the current one up.
// eslint-disable-next-line no-console
console.info("%c[vaultpod-viewer] build " + __BUILD_ID__, "color:#27a567;font-weight:bold");

import { Router, Route } from "@solidjs/router";
import { QueryClient, QueryClientProvider } from "@tanstack/solid-query";
import { App } from "./App";
import { StudyList } from "./app/StudyList";
import { StudyViewer } from "./app/StudyViewer";
import { WorklistPage } from "./app/WorklistPage";
import { ReportsPage } from "./app/ReportsPage";
import { AdminPage } from "./app/AdminPage";
import { Login } from "./app/Login";
import { Callback } from "./app/Callback";
import { RoleLanding } from "./app/RoleLanding";
import { PathologyReadingPage } from "./app/PathologyReadingPage";
import { ClinicianPage } from "./app/ClinicianPage";
import { PatientPortalPage } from "./app/PatientPortalPage";
import { AuditorPage } from "./app/AuditorPage";
import { AdminDashboard } from "./app/AdminDashboard";
import { TechnologistPage } from "./app/TechnologistPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 10 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function Root() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router root={App}>
        <Route path="/login"    component={Login} />
        <Route path="/callback" component={Callback} />

        {/* Role-aware root — redirects to the landing page that
            matches the user's strongest granted role. */}
        <Route path="/" component={RoleLanding} />

        {/* Per-role entry points. Each page enforces its own UI via
            `can()` checks; backend still enforces the permission
            matrix through X-VaultPod-Claims. */}
        <Route path="/reading"   component={PathologyReadingPage} />
        <Route path="/referrals" component={ClinicianPage} />
        <Route path="/portal"    component={PatientPortalPage} />
        <Route path="/audit"     component={AuditorPage} />
        <Route path="/admin"     component={AdminDashboard} />
        <Route path="/techbench" component={TechnologistPage} />

        {/* Shared pages */}
        <Route path="/studies" component={StudyList} />
        <Route path="/study/:studyUid" component={StudyViewer} />
        <Route path="/worklist" component={WorklistPage} />
        <Route path="/reports" component={ReportsPage} />
      </Router>
    </QueryClientProvider>
  );
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root not found");
render(() => <Root />, rootEl);
